import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Building2, CheckCircle2 } from 'lucide-react';
import { leisureServiceSchema, breadcrumbSchema } from '../utils/structuredData';

const LeisurePage = () => {
  const features = [
    'Luxury hotel and resort scanning',
    'Spa and wellness facility documentation',
    'Renovation planning and design',
    'Virtual tours for marketing',
    'Space optimization studies',
    'Heritage preservation'
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Leisure & Hospitality 3D Virtual Tours | Hotels, Spas & Resorts"
        description="Matterport 3D virtual tours for hotels, spas and resorts across the UK and Europe. Boost marketing and plan renovations. Get a free quote."
        keywords="hotel 3D virtual tour, spa scanning, resort virtual tour, hospitality 3D scanning, Matterport hotel, leisure venue scanning, luxury hotel virtual tour, hospitality marketing technology"
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/leisure` : ''}
        structuredData={[
          leisureServiceSchema,
          breadcrumbSchema([
            { name: 'Home', url: typeof window !== 'undefined' ? window.location.origin : '' },
            { name: 'Leisure & Hospitality', url: typeof window !== 'undefined' ? `${window.location.origin}/leisure` : '' }
          ])
        ]}
      />
      <Navbar scrolled={true} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-orange-50 rounded-full mb-6">
              <Building2 className="h-6 w-6 text-orange-600" />
              <span className="text-orange-600 font-semibold">LEISURE & HOSPITALITY</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              3D Scanning for Hotels, Spas & Resorts
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive scanning solutions for hotels, spas, resorts and hospitality venues.
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-16">
            <img
              src="https://images.unsplash.com/photo-1532926381893-7542290edf1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcGF8ZW58MHx8fHwxNzcyNzE5NDI1fDA&ixlib=rb-4.1.0&q=85&w=1400"
              alt="Luxury spa interior captured with Matterport 3D virtual tour technology"
              className="w-full h-[500px] object-cover"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Elevate Your Hospitality Venue
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  The leisure and hospitality sector demands precision and attention to detail. Our 3D scanning services capture every aspect of hotels, spas, and resorts with stunning accuracy.
                </p>
                <p>
                  Whether you're planning renovations, creating virtual tours for marketing, or documenting historic properties, our digital scanning technology provides the detailed records you need.
                </p>
                <p>
                  We work with hotel groups, spa operators, and resort developers to deliver high-quality digital assets that support design, planning, and promotional activities.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Leisure Scanning Services</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              <button className="mt-8 w-full px-8 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-orange-500/50 transition-all duration-300">
                Request a Quote
              </button>
            </div>
          </div>
          {/* Interactive 3D Model Example */}
          <div className="mt-20">
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-12">
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Interactive 3D Model Example</h2>
              <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
                Explore our 3D scan of a luxury Event Space directly below
              </p>
              <div className="bg-white rounded-2xl p-4 shadow-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Luxury Event Space
                </h3>
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src="https://digitalscanninggroup.com/3d-model/event-space/skinned/"
                    className="absolute top-0 left-0 w-full h-full rounded-xl border-2 border-gray-200"
                    title="Luxury Event Space - 3D Model"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <p className="text-gray-600 mt-4 text-center">
                  Use your mouse to navigate • Scroll to zoom • Click and drag to rotate
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LeisurePage;
